import * as React from 'react';
import { type ButtonProps as AriaButtonProps, TooltipContext } from 'react-aria-components';

/**
 * A natively disabled button swallows pointer events, so a disabled or
 * loading button inside a TooltipTrigger never shows its tooltip. When a
 * tooltip is present, simulate both states instead: keep the button enabled
 * with `aria-disabled` (so assistive tech still reports it as disabled), and
 * add the returned class to the button so `pointer-events-none` styles don't
 * block hover either.
 *
 * Because react-aria gates its own handlers on `isDisabled` — deliberately
 * false here — the simulated state must neutralize every press-path prop a
 * consumer could pass (`onPress`, `onPressStart`, `onPressEnd`, `onPressUp`,
 * `onClick`) and force `type="button"` so a submit button cannot submit
 * mid-flight (RAC's own Button does the same for `isPending`). Callers must
 * spread `disabledProps` after the consumer's props so these overrides win.
 */
function useTooltipDisabledProps({ isDisabled, isLoading }: { isDisabled: boolean; isLoading: boolean }): {
  disabledProps: Pick<
    AriaButtonProps,
    'isDisabled' | 'onPress' | 'onPressStart' | 'onPressEnd' | 'onPressUp' | 'onClick' | 'type'
  > & { 'aria-disabled'?: 'true' };
  tooltipTriggerClassName: string | undefined;
} {
  const hasTooltip = !!React.useContext(TooltipContext);
  const isInert = isDisabled || isLoading;

  return {
    disabledProps: hasTooltip
      ? {
          isDisabled: false,
          ...(isInert
            ? {
                onPress: () => {},
                onPressStart: () => {},
                onPressEnd: () => {},
                onPressUp: () => {},
                onClick: () => {},
                type: 'button' as const,
                'aria-disabled': 'true' as const,
              }
            : {}),
        }
      : { isDisabled: isInert },
    tooltipTriggerClassName: hasTooltip ? '!pointer-events-auto' : undefined,
  };
}

export { useTooltipDisabledProps };
